import { useSelectModeContext } from './useSelectModeContext';
import styles from './SelectModeOverlay.module.scss';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { FileNodeState, isFileState } from './SelectionStore';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import DeleteIcon from 'assets/icons/delete.svg?react';
import DownloadIcon from 'assets/icons/download_24.svg?react';
import { AppMenu, AppMenuItem } from 'components/uikit/menu/AppMenu';
import { MenuDirection } from 'components/uikit/menu/MenuDirection';
import Dots from 'assets/icons/dots.svg?react';
import { QueryFactory } from 'services/api';
import {
  DocumentExportFormat,
  DocumentListItemDto,
  FileResponse,
  OperationType,
  ProjectResourceType,
  VariableListItemDto,
} from 'services/api/api-client.types';
import { triggerDownloadFileDialog } from 'helpers/file-helper';
import { BulkUpdateStatusModal } from './modals/updateStatus/BulkUpdateStatusModal.tsx';
import {
  UpdateFieldType,
  useConfirmSigningDocumentsBulkUpdate,
} from './modals/updateStatus/useConfirmSigningDocumentsBulkUpdate.tsx';
import { useModal } from 'components/uikit/modal/useModal';
import { ButtonColor } from 'components/uikit/buttons/Button';
import { ImportIntoProjectModal } from 'pages/authorized/templates/TemplateList/ImportIntoProjectModal/ImportIntoProjectModal.tsx';
import { DownloadFilesModal } from './modals/downloadFiles/DownloadFilesModal.tsx';
import { VariableDocumentList } from '../../../variables/components/variableDocumentsList/VariableDocumentsList.tsx';
import { BulkUpdateDateModal } from './modals/updateDate/BulkUpdateDateModal.tsx';
import { BulkUpdateVersionModal } from './modals/updateVersion/BulkUpdateVersionModal.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import { PermissionGate } from 'helpers/projectPermissions/PermissionGate.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';

export const SelectModeOverlay = (props: {
  revisionId: number;
  projectId: number;
  setIsLoading: (isLoading: boolean) => void;
  onBulkRemove: (
    itemsToDelete: string[],
    onDelete?: () => Promise<void>,
  ) => Promise<void>;
  revisionDocuments: DocumentListItemDto[];
  revisionVariables: VariableListItemDto[];
}) => {
  const i18n = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.FoldersTree.SelectModeOverlay',
  );

  const { store, isSelectMode, setIsSelectMode } = useSelectModeContext();
  const selectedRootNames = store
    .getSelectedNodes()
    .filter((node) => node.parentId === undefined)
    .map((node) => node.name);

  const [isMenuShown, setIsMenuShown] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null!);
  const modal = useModal();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [openedUpdateModalType, setOpenedUpdateModalType] =
    useState<UpdateFieldType | null>(null);

  const { onBulkUpdateWithConfirm, getNonSigningDocuments } =
    useConfirmSigningDocumentsBulkUpdate({
      afterConfirm: (updatedFieldType) => {
        setIsMenuShown(false);

        const selectedNonSigningDocuments = getNonSigningDocuments();
        if (selectedNonSigningDocuments.length === 0) {
          setIsSelectMode(false);
          return;
        }

        switch (updatedFieldType) {
          case UpdateFieldType.Version:
            setOpenedUpdateModalType(UpdateFieldType.Version);
            break;
          case UpdateFieldType.Date:
            setOpenedUpdateModalType(UpdateFieldType.Date);
            break;
          case UpdateFieldType.Status:
            setOpenedUpdateModalType(UpdateFieldType.Status);
            break;
        }
      },
      revisionDocuments: props.revisionDocuments,
      projectId: props.projectId,
      revisionId: props.revisionId,
    });

  const menuItems: AppMenuItem[] = useMemo(() => {
    return [
      {
        key: 'export_to_project',
        text: i18n.t('Buttons.export_to_project'),
        onClick: async () => {
          setIsMenuShown(false);
          setIsImportModalOpen(true);
        },
      },
      {
        key: 'update_version',
        text: i18n.t('Buttons.update_version'),
        onClick: () => onBulkUpdateWithConfirm(UpdateFieldType.Version),
      },
      {
        key: 'update_date',
        text: i18n.t('Buttons.update_date'),
        onClick: () => onBulkUpdateWithConfirm(UpdateFieldType.Date),
      },
      {
        key: 'update_status',
        text: i18n.t('Buttons.update_status'),
        onClick: () => onBulkUpdateWithConfirm(UpdateFieldType.Status),
      },
    ];
  }, [i18n]);

  const selectedFileCount = useSyncExternalStore(
    useCallback(
      (l) => {
        const unsubs = [...store['nodes'].values()]
          .filter(isFileState)
          .map((n) => store.subscribe(n.id, l));
        return () => unsubs.forEach((fn) => fn());
      },
      [store],
    ),
    useCallback(() => store.getSelectedFiles().length, [store]),
  );

  const totalSelectedCount = useSyncExternalStore(
    useCallback(
      (l) => {
        const unsubs = [...store['nodes'].values()].map((n) =>
          store.subscribe(n.id, l),
        );
        return () => unsubs.forEach((fn) => fn());
      },
      [store],
    ),
    useCallback(() => store.getSelectedNodes().length, [store]),
  );

  const onDownload = async (fileFormat: DocumentExportFormat) => {
    props.setIsLoading(true);
    const selectionEntries = store.getSelectionEntries();
    var zip: FileResponse;

    try {
      zip = await QueryFactory.DocumentExportClient.deferExportDocumentTree(
        {
          selections: selectionEntries,
          revisionId: props.revisionId,
        },
        fileFormat,
      );
    } catch (e) {
      showErrorToast(e);
      return;
    }

    const newBlob = new Blob([zip.data], {
      type: zip.data.type,
    });

    triggerDownloadFileDialog(newBlob, zip.fileName ?? 'documents.zip');
    props.setIsLoading(false);
    setIsSelectMode(false);
  };

  const onDelete = async () => {
    const nodesToDelete = store.getSelectedNodes();
    const documentNodes = nodesToDelete.filter((n): n is FileNodeState =>
      isFileState(n),
    );
    const documentsToDelete = documentNodes.map((n) => n.documentId);

    if (documentNodes.length > 0) {
      const result = await modal.showConfirm({
        title: i18n.t('DeleteConfirmation.title'),
        text: (
          <div className={styles.deleteConfirmation}>
            <a>{i18n.t('DeleteConfirmation.message')}</a>
            <VariableDocumentList
              documents={documentNodes.map((n) => ({
                id: n.documentId,
                name: n.name,
                // Can be null here because this is confirmation in scope of current revision
                revisionVersion: null,
                projectId: props.projectId,
                revisionId: props.revisionId,
              }))}
            />
          </div>
        ),
        okButtonText: i18n.t('DeleteConfirmation.confirm_button'),
        okButtonColor: ButtonColor.DestroyPrimary,
        cancelButtonColor: ButtonColor.Secondary,
      });

      if (!result) return;
    }

    props.setIsLoading(true);
    await props.onBulkRemove(
      nodesToDelete.map((n) => n.id),
      async () => {
        if (documentsToDelete.length === 0) return;
        await QueryFactory.DocumentClient.bulkDelete(documentsToDelete);
      },
    );
    props.setIsLoading(false);

    setIsSelectMode(false);
  };

  return (
    <div className={styles.overlayRoot} data-select-mode={isSelectMode}>
      <div className={styles.counter}>
        {i18n.t(
          selectedFileCount === 1
            ? 'ItemCounter.singular'
            : 'ItemCounter.plural',
          {
            count: selectedFileCount,
          },
        )}
      </div>
      <div className={styles.buttonContainer}>
        <TextButton
          disabled={totalSelectedCount === 0}
          title={i18n.t('Buttons.download')}
          color={TextButtonColor.Primary}
          icon={<DownloadIcon />}
          className={styles.button}
          onClick={() => setIsDownloadModalOpen(true)}
        />
      </div>
      <PermissionGate
        permissions={[
          permission(ProjectResourceType.Document, OperationType.Delete),
        ]}
      >
        <div className={styles.buttonContainer}>
          <TextButton
            disabled={totalSelectedCount === 0}
            title={i18n.t('Buttons.delete')}
            color={TextButtonColor.Destroy}
            icon={<DeleteIcon />}
            className={styles.button}
            onClick={onDelete}
          />
        </div>
      </PermissionGate>
      <PermissionGate
        permissions={[
          permission(
            ProjectResourceType.Document,
            OperationType.Update,
            OperationType.Create,
          ),
          permission(ProjectResourceType.Variable, OperationType.Update),
        ]}
      >
        <div className={styles.buttonContainer}>
          <div
            className={styles.dotMenu}
            ref={menuRef}
            onClick={() => {
              if (totalSelectedCount === 0) return;
              setIsMenuShown((x) => !x);
            }}
            aria-disabled={totalSelectedCount === 0}
          >
            <Dots />
          </div>
          <AppMenu
            open={isMenuShown}
            menuItems={menuItems}
            direction={MenuDirection.topCenter}
            anchorEl={menuRef.current}
            onClose={() => setIsMenuShown(false)}
          />
        </div>
      </PermissionGate>
      {isDownloadModalOpen && (
        <DownloadFilesModal
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
          onDownload={onDownload}
        />
      )}
      {isImportModalOpen && (
        <ImportIntoProjectModal
          onImport={async (latestRevision) => {
            props.setIsLoading(true);
            await QueryFactory.RevisionClient.importFromEntries(
              latestRevision.id,
              store.getSelectionEntries(),
            );
            props.setIsLoading(false);
            setIsSelectMode(false);
          }}
          title={i18n.t('Buttons.export_to_project')}
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          currentProjectId={props.projectId}
          importingNames={selectedRootNames}
        />
      )}
      {openedUpdateModalType === UpdateFieldType.Status && (
        <BulkUpdateStatusModal
          selectedDocuments={getNonSigningDocuments()}
          isOpen={openedUpdateModalType === UpdateFieldType.Status}
          onClose={() => setOpenedUpdateModalType(null)}
        />
      )}
      {openedUpdateModalType === UpdateFieldType.Version && (
        <BulkUpdateVersionModal
          isOpen={openedUpdateModalType === UpdateFieldType.Version}
          onClose={() => setOpenedUpdateModalType(null)}
          revisionId={props.revisionId}
          selectedDocuments={getNonSigningDocuments()}
        />
      )}
      {openedUpdateModalType === UpdateFieldType.Date && (
        <BulkUpdateDateModal
          isOpen={openedUpdateModalType === UpdateFieldType.Date}
          onClose={() => setOpenedUpdateModalType(null)}
          revisionId={props.revisionId}
          revisionDocuments={props.revisionDocuments}
          revisionVariables={props.revisionVariables}
          selectedDocuments={getNonSigningDocuments()}
        />
      )}
    </div>
  );
};
